import React from 'react'
import { Link } from 'react-router-dom'
import { Col, Row } from 'reactstrap'
import { Moment } from 'moment'
import ServiceBasicView from './ServiceBasicView'
import ServiceAdditionalDetails from './ServiceAdditionalDetails'
import ServiceBroadbandPanel from './ServiceBroadbandPanel'
import ServiceDnsPanel from './ServiceDnsPanel'
import ServiceGraphPanel from './ServiceGraphPanel'
import ServiceVeeamPanel from './ServiceVeeamPanel'
import {
    SERVICE_KINDS,
    TServiceDetail,
    TServiceGraphHistory,
    TServiceUsageData,
} from '../../types/serviceView'
import {
    flattenServiceMetadata,
    hasRenderableBillingDetails,
} from '../../helpers/services/detail'
import { TServiceKeyValueItem } from './ServiceKeyValueCard'
import Money from '../../helpers/Money'

type TGraphDateRangeHandler = React.ComponentProps<
    typeof ServiceGraphPanel
>['onGraphDateRangeChange']

type TRendererProps = {
    service: TServiceDetail
    showBillingDetails: boolean
    usage: TServiceUsageData | null
    usageLoading: boolean
    usageError: string
    month: string
    year: string
    onMonthChange: (value: string) => void
    onYearChange: (value: string) => void
    selectedGraphId: string
    onGraphChange: (graphId: string) => void
    graphHistory: TServiceGraphHistory | null
    graphLoading: boolean
    graphError: string
    graphStartDate: Moment
    graphEndDate: Moment
    onGraphDateRangeChange: TGraphDateRangeHandler
}

const getAdditionalDetailsExclusions = (
    service: TServiceDetail
): Set<string> => {
    if (service.resolvedKind === SERVICE_KINDS.VEEAM) {
        return new Set([
            'storageQuota',
            'usedStorageQuota',
            'vms',
            'rentalPeriod',
        ])
    }

    return new Set(['bEndDeviceId'])
}

const getToneForMetadata = (
    key: string,
    value: string
): TServiceKeyValueItem['tone'] => {
    if (key === 'status') {
        return value.toLowerCase() === 'online' ? 'success' : 'danger'
    }

    return 'default'
}

const formatMetadataValue = (
    service: TServiceDetail,
    key: string,
    value: string
): React.ReactNode => {
    if (key === 'transferLimit' && Number(value) === 0) {
        return 'Unlimited'
    }

    if (key === 'bEnd' && service.additionalDetails?.bEndDeviceId) {
        return (
            <Link to={`/monitoring/${service.additionalDetails.bEndDeviceId}`}>
                {value}
            </Link>
        )
    }

    return value
}

const buildBillingItems = (
    service: TServiceDetail,
    showBillingDetails: boolean
): TServiceKeyValueItem[] => {
    if (!showBillingDetails || !hasRenderableBillingDetails(service.billing)) {
        return []
    }

    const billing = service.billing
    const items: TServiceKeyValueItem[] = []

    if (billing?.install !== undefined) {
        items.push({
            label: 'Install',
            value: Money.format(billing.install),
        })
    }

    if (billing?.rental !== undefined) {
        items.push({
            label: 'Rental',
            value: Money.format(billing.rental),
        })
    }

    return items
}

const buildAdditionalDetailItems = (
    service: TServiceDetail
): TServiceKeyValueItem[] => {
    const excludedKeys = getAdditionalDetailsExclusions(service)

    return flattenServiceMetadata(service.additionalDetails)
        .filter((item) => !excludedKeys.has(item.key))
        .map((item) => ({
            label: item.label,
            value: formatMetadataValue(service, item.key, item.value),
            tone: getToneForMetadata(item.key, item.value),
        }))
}

const renderSpecializedPanel = (
    props: TRendererProps
): React.ReactNode => {
    const { service } = props

    if (service.resolvedKind === SERVICE_KINDS.VEEAM) {
        return <ServiceVeeamPanel service={service} />
    }

    if (service.resolvedKind === SERVICE_KINDS.BROADBAND) {
        return (
            <ServiceBroadbandPanel
                usage={props.usage}
                loading={props.usageLoading}
                error={props.usageError}
                month={props.month}
                year={props.year}
                onMonthChange={props.onMonthChange}
                onYearChange={props.onYearChange}
            />
        )
    }

    if (service.resolvedKind === SERVICE_KINDS.DNS) {
        return <ServiceDnsPanel />
    }

    return null
}

const ServiceDetailRenderer: React.FC<TRendererProps> = (props) => {
    const additionalDetailItems = buildAdditionalDetailItems(props.service)
    const billingItems = buildBillingItems(
        props.service,
        props.showBillingDetails
    )
    const hasAdditionalDetails = additionalDetailItems.length > 0
    const specializedPanel = renderSpecializedPanel(props)

    return (
        <>
            <Row>
                <Col lg={7} xl={6} className="mb-4">
                    <ServiceBasicView
                        service={props.service}
                        extraItems={billingItems}
                    />
                </Col>
                {hasAdditionalDetails ? (
                    <Col lg={5} xl={6} className="mb-4">
                        <ServiceAdditionalDetails
                            items={additionalDetailItems}
                        />
                    </Col>
                ) : null}
            </Row>
            {specializedPanel}
            {props.service.graphs.length > 0 ? (
                <ServiceGraphPanel
                    service={props.service}
                    selectedGraphId={props.selectedGraphId}
                    onGraphChange={props.onGraphChange}
                    graphHistory={props.graphHistory}
                    graphLoading={props.graphLoading}
                    graphError={props.graphError}
                    graphStartDate={props.graphStartDate}
                    graphEndDate={props.graphEndDate}
                    onGraphDateRangeChange={props.onGraphDateRangeChange}
                />
            ) : null}
        </>
    )
}

export default ServiceDetailRenderer
